import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DeliveryReportService, SentBucket } from '../../../../../services/reports/delivery-report.service';
import { LiveRefresh } from '../../../../../services/live/live-refresh';
import { formatClock, formatCount, formatCountdown, formatWhen, sendMoment } from '../../../../../../utils/sms';
import { DashMessage } from '../../dash-message';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
/** Hourly counts are fetched again this often while the page is open. */
const REFRESH_MS = 60_000;
/** Closer than this (percent of the line) and a time label would run into the one before. */
const LABEL_GAP = 8;
/** Room the next send's countdown and subject take up beside its marker. */
const LEAD_LABEL_WIDTH = 22;
const NARROW = '(max-width: 639px)';

export interface OutboxBar {
  start: Date;
  left: number;
  width: number;
  total: number;
  delivered: number;
  pending: number;
  failed: number;
}

export interface OutboxMark {
  message: DashMessage;
  at: Date;
  left: number;
  /** Time of day beside the marker; empty when it would collide with the one before. */
  clock: string;
  description: string;
}

export interface OutboxTick {
  left: number;
  clock: string;
  /** "Today", "Tomorrow"…: only on midnights. */
  day: string;
}

function startOfDay(time: number): number {
  const day = new Date(time);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });

/**
 * The outbox around now: hourly sends over the last day on the left, the
 * scheduled sends of the next day on the right, and a live line at now
 * between them. Sent and missed messages sit on the line as pins.
 */
@Component({
  selector: 'dash-outbox-line',
  standalone: true,
  templateUrl: './outbox-line.component.html',
  styleUrl: './outbox-line.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutboxLineComponent implements OnInit, OnChanges, OnDestroy {
  private reports = inject(DeliveryReportService);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  /** The page's clock: the line is drawn around it. */
  @Input() now = new Date();
  /** Recent messages, in any order. */
  @Input() recent: DashMessage[] = [];
  /** Scheduled sends, soonest first. */
  @Input() queue: DashMessage[] = [];
  /** Schedules that passed without going out. */
  @Input() overdue: DashMessage[] = [];
  /** The queue is still loading: "Nothing scheduled" must not flash. */
  @Input() queueLoading = false;
  @Output() open = new EventEmitter<DashMessage>();

  /** Hours shown either side of now: a day on wide screens, half a day on phones. */
  hours = 24;
  state: 'loading' | 'ready' | 'unavailable' | 'failed' = 'loading';
  /** Bars grow in once, on the first load. */
  intro = true;
  /** The hour under the pointer, read out in place of the totals. */
  activeBar: OutboxBar | null = null;

  bars: OutboxBar[] = [];
  sent: OutboxMark[] = [];
  missed: OutboxMark[] = [];
  upcoming: OutboxMark[] = [];
  ticks: OutboxTick[] = [];
  /** Scheduled sends after the window. */
  later = 0;
  /** With nothing scheduled inside the window: the first send after it. */
  beyond: DashMessage | null = null;
  /** With nothing sent inside the window: the newest send before it. */
  before: DashMessage | null = null;
  maxTotal = 0;
  pastTotal = 0;
  pastDelivered = 0;
  pastFailed = 0;

  readonly formatCount = formatCount;

  private buckets: SentBucket[] = [];
  private live?: LiveRefresh;
  private request?: Subscription;
  private introTimer?: ReturnType<typeof setTimeout>;
  private media?: MediaQueryList;
  private readonly onMedia = () => this.setHours(this.media?.matches ? 12 : 24);

  get clock(): string {
    return formatClock(this.now);
  }

  get deliveredShare(): string {
    if (!this.pastTotal) return '0%';
    const share = (this.pastDelivered / this.pastTotal) * 100;
    return `${share >= 99.5 || share < 1 ? Math.round(share) : Math.floor(share)}%`;
  }

  get nextUp(): OutboxMark | null {
    return this.upcoming[0] ?? null;
  }

  /** What the whole figure says, for screen readers. */
  get summary(): string {
    const past =
      this.state === 'ready'
        ? `${formatCount(this.pastTotal)} sent, ${this.deliveredShare} delivered, ${formatCount(this.pastFailed)} failed`
        : `${this.sent.length} ${this.sent.length === 1 ? 'send' : 'sends'}`;
    const next = this.nextUp
      ? `${this.upcoming.length} scheduled, the next ${this.subjectOf(this.nextUp.message)} ${this.countdown(this.nextUp.at)}`
      : 'nothing scheduled';
    return `Outbox. Last ${this.hours} hours: ${past}. Next ${this.hours} hours: ${next}.`;
  }

  subjectOf(message: DashMessage | null): string {
    return message?.messageSubject || 'Untitled message';
  }

  countdown(at: Date): string {
    return formatCountdown(at, this.now);
  }

  whenOf(message: DashMessage | null): string {
    return formatWhen(sendMoment(message), this.now);
  }

  barHeight(bar: OutboxBar): number {
    return this.maxTotal ? (bar.total / this.maxTotal) * 100 : 0;
  }

  share(part: number, bar: OutboxBar): number {
    return bar.total ? (part / bar.total) * 100 : 0;
  }

  barRange(bar: OutboxBar): string {
    return `${formatClock(bar.start)}–${formatClock(new Date(bar.start.getTime() + HOUR))}`;
  }

  ngOnInit(): void {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.media = window.matchMedia(NARROW);
      this.hours = this.media.matches ? 12 : 24;
      this.media.addEventListener?.('change', this.onMedia);
    }
    this.build();
    this.load();
    this.live = new LiveRefresh(this.ngZone, {
      refresh: () => this.load(true),
      interval: () => REFRESH_MS,
    });
    this.live.start();
  }

  ngOnChanges(): void {
    this.build();
  }

  ngOnDestroy(): void {
    this.live?.stop();
    this.request?.unsubscribe();
    this.media?.removeEventListener?.('change', this.onMedia);
    if (this.introTimer) clearTimeout(this.introTimer);
  }

  /** `silent`: a background refresh keeps what is shown until new counts arrive. */
  load(silent = false): void {
    if (!silent) this.state = 'loading';
    const to = new Date();
    const from = new Date(to.getTime() - this.hours * HOUR);
    from.setMinutes(0, 0, 0);

    this.request?.unsubscribe();
    this.request = this.reports.sentOverview(from, to, 'hour').subscribe({
      next: (overview) => {
        this.buckets = overview.buckets;
        this.state = 'ready';
        this.build();
        this.endIntro();
        this.cdr.markForCheck();
      },
      error: (error: { status?: number }) => {
        if (silent && this.state === 'ready') return;
        // The endpoint ships with the backend update; until then the line shows pins only.
        this.state = error?.status === 404 ? 'unavailable' : 'failed';
        this.buckets = [];
        this.build();
        this.cdr.markForCheck();
      },
    });
  }

  showBar(bar: OutboxBar | null): void {
    this.activeBar = bar;
  }

  private setHours(hours: number): void {
    if (hours === this.hours) return;
    this.ngZone.run(() => {
      this.hours = hours;
      this.load(true);
      this.build();
      this.cdr.markForCheck();
    });
  }

  private endIntro(): void {
    if (!this.intro || this.introTimer) return;
    this.introTimer = setTimeout(() => {
      this.intro = false;
      this.cdr.markForCheck();
    }, 1200);
  }

  private build(): void {
    const now = this.now.getTime();
    const span = this.hours * HOUR;
    const start = now - span;
    const end = now + span;
    const at = (time: number) => ((time - start) / (2 * span)) * 100;

    // ── Past: one bar per hour, cut at the window's edge and at now ──
    this.bars = this.buckets
      .map((bucket) => {
        const from = Math.max(bucket.start.getTime(), start);
        const to = Math.min(bucket.start.getTime() + HOUR, now);
        return {
          start: bucket.start,
          left: at(from),
          width: Math.max(0, at(to) - at(from)),
          total: bucket.total,
          delivered: bucket.delivered,
          failed: bucket.failed,
          pending: Math.max(0, bucket.total - bucket.delivered - bucket.failed),
        };
      })
      .filter((bar) => bar.width > 0);
    this.maxTotal = Math.max(0, ...this.bars.map((bar) => bar.total));
    this.pastTotal = this.buckets.reduce((sum, bucket) => sum + bucket.total, 0);
    this.pastDelivered = this.buckets.reduce((sum, bucket) => sum + bucket.delivered, 0);
    this.pastFailed = this.buckets.reduce((sum, bucket) => sum + bucket.failed, 0);

    const timed = (messages: DashMessage[]) =>
      messages
        .map((message) => ({ message, time: sendMoment(message)?.getTime() }))
        .filter((entry): entry is { message: DashMessage; time: number } => entry.time !== undefined)
        .sort((a, b) => a.time - b.time);

    const mark = (message: DashMessage, time: number, clock: string, description: string): OutboxMark => ({
      message,
      at: new Date(time),
      left: at(Math.min(Math.max(time, start), end)),
      clock,
      description,
    });

    // ── Pins on the line: sends that went out, and schedules that did not ──
    const went = timed(this.recent.filter((message) => /^(completed|active)$/i.test(message?.status ?? '')));
    this.sent = went
      .filter(({ time }) => time >= start && time <= now)
      .map(({ message, time }) =>
        mark(message, time, formatClock(new Date(time)), `${this.subjectOf(message)}, sent ${formatWhen(new Date(time), this.now)}`)
      );
    const newest = went[went.length - 1];
    this.before = !this.sent.length && newest && newest.time < start ? newest.message : null;

    this.missed = timed(this.overdue)
      .filter(({ time }) => time >= start && time <= now)
      .map(({ message, time }) =>
        mark(
          message,
          time,
          formatClock(new Date(time)),
          `${this.subjectOf(message)}, was due ${formatWhen(new Date(time), this.now)} and hasn’t gone out`
        )
      );

    // ── Future: scheduled sends. One just due sits on the now line ──
    const queued = timed(this.queue);
    const inside = queued.filter(({ time }) => time <= end);
    // Labels run rightwards from their marker; one that would start inside the
    // label before it is left off (the marker keeps its tooltip).
    let labelEnd = -Infinity;
    this.upcoming = inside.map(({ message, time }, index) => {
      const left = at(Math.max(time, now));
      const fits = index === 0 || left >= labelEnd;
      if (fits) labelEnd = left + (index === 0 ? LEAD_LABEL_WIDTH : LABEL_GAP);
      const clock = fits ? formatClock(new Date(time)) : '';
      return mark(
        message,
        Math.max(time, now),
        clock,
        `${this.subjectOf(message)}, scheduled ${formatWhen(new Date(time), this.now)}, ${formatCountdown(new Date(time), this.now)}`
      );
    });
    this.later = queued.length - inside.length;
    this.beyond = !inside.length && queued.length ? queued[0].message : null;

    // ── Axis: every six hours (three on phones), with the day at midnight ──
    const step = this.hours >= 24 ? 6 : 3;
    const first = new Date(start);
    first.setMinutes(0, 0, 0);
    while (first.getTime() < start || first.getHours() % step !== 0) first.setHours(first.getHours() + 1);
    const ticks: OutboxTick[] = [];
    for (const tick = new Date(first); tick.getTime() <= end; tick.setHours(tick.getHours() + step)) {
      ticks.push({
        left: at(tick.getTime()),
        clock: formatClock(tick),
        day: tick.getHours() === 0 ? this.dayName(tick.getTime(), now) : '',
      });
    }
    this.ticks = ticks;
    if (this.activeBar && !this.bars.some((bar) => bar.start.getTime() === this.activeBar?.start.getTime())) {
      this.activeBar = null;
    }
  }

  private dayName(time: number, now: number): string {
    const days = Math.round((startOfDay(time) - startOfDay(now)) / DAY);
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    return weekday.format(new Date(time));
  }
}
